import { useCallback, useEffect, useMemo, useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';

import CollapseIconBordered from '../../../../../components/svgs/CollapseIconBordered';
import DeleteIconBordered from '../../../../../components/svgs/DeleteIconBordered';
import EditIconBordered from '../../../../../components/svgs/EditIconBordered';
import { FORTA_ERROR, FORTA_GRAY1, FORTA_WHITE } from '../../../../../utils/colors';
import { LIST_ROW_BG_COLORS } from '../../../styles';
import { getAllFieldsTouched } from '../../../utils';
import { useFormReadOnly } from '../../formReadOnlyProvider';
import { useAutoSaveRow } from '../hooks';
import {
  AUTO_SAVE_ROW_ACTIVE_ICON_SX,
  AUTO_SAVE_ROW_ICON_BUTTON_SX,
  getAutoSaveExpandIconSx,
  getAutoSaveFieldsSx,
  getAutoSaveRowSx,
} from '../styles';

import type { FormikValues } from 'formik';
import type { AutoSaveListRowProps } from './types';

export default function AutoSaveListRow<TValues extends FormikValues, TRow, TPayload extends object>({
  config,
  row,
  savedRow,
  index,
  disabled,
  onRequestDelete,
  onDiscardDraft,
  onCreated,
  onValidityChange,
  onPersistedChange,
}: AutoSaveListRowProps<TValues, TRow, TPayload>) {
  const readOnly = useFormReadOnly();
  const isDraftSlot = row == null;
  // A draft that hasn't persisted its first field yet: only removable, no collapse.
  const isUnsavedDraft = row == null && savedRow == null;
  const effectiveRow = row ?? savedRow;
  // Depend on the used members, not the whole config object — config is the list's props
  // object and changes identity every parent render, which would retrigger the
  // row-sync (resetForm) effect below on renders where the row didn't change.
  const { mapRowToValues, emptyValues } = config;
  const initialValues = useMemo(
    () => (effectiveRow ? mapRowToValues(effectiveRow) : emptyValues),
    [effectiveRow, mapRowToValues, emptyValues],
  );
  const formik = useFormik<TValues>({
    initialValues,
    validationSchema: config.validationSchema,
    validateOnMount: true,
    onSubmit: () => {},
  });

  // After any save, mark every field touched so still-missing required fields highlight.
  const revealErrors = useCallback(() => {
    void formik.setTouched(getAllFieldsTouched(formik.values), true);
  }, [formik]);

  const { saveField, getChangedFieldNames, isCreating } = useAutoSaveRow<TValues, TPayload>({
    rowId: effectiveRow ? config.getRowKey(effectiveRow) : undefined,
    formik,
    validationSchema: config.validationSchema,
    toFieldPayload: config.toFieldPayload,
    onCreate: config.onCreate,
    onUpdate: config.onUpdate,
    onCreated,
    onSaved: revealErrors,
  });

  // Report schema validity synchronously — formik.isValid stays true until its
  // async validation resolves, which would briefly enable the add button.
  useEffect(() => {
    onValidityChange?.(config.validationSchema.isValidSync(formik.values));
  }, [config.validationSchema, formik.values, onValidityChange]);

  // Report whether every change has actually persisted — false while a field save is
  // still in flight or has failed — so the list can hold its add button until the row
  // is fully written. Successful saves re-render (touched updates), refreshing this.
  const isPersisted = getChangedFieldNames(formik.values).length === 0;
  useEffect(() => {
    onPersistedChange?.(isPersisted);
  }, [isPersisted, onPersistedChange]);

  const [expanded, setExpanded] = useState(isDraftSlot);
  const [editing, setEditing] = useState(isDraftSlot);

  // A row can mount while its last field save is still in flight; useFormik never
  // reinitializes on its own, so without this the fields would stay frozen at the
  // mount-time snapshot even after the store row catches up. Skipped while editing
  // so a refresh can't stomp values the user is typing. resetForm clears formik.errors,
  // so revalidate right after — otherwise the row's error indicator vanishes.
  const { resetForm, validateForm } = formik;
  useEffect(() => {
    if (!editing) {
      resetForm({ values: initialValues });
      void validateForm(initialValues);
    }
  }, [editing, initialValues, resetForm, validateForm]);

  const handleExpandToggle = useCallback(() => {
    if (expanded) {
      setExpanded(false);
      setEditing(false);
    } else {
      setExpanded(true);
    }
  }, [expanded]);

  const handleEdit = useCallback(() => {
    if (editing) {
      setEditing(false);
      return;
    }
    setExpanded(true);
    setEditing(true);
    revealErrors();
  }, [editing, revealErrors]);

  const handleDelete = useCallback(() => {
    if (effectiveRow) {
      onRequestDelete(effectiveRow);
    }
  }, [effectiveRow, onRequestDelete]);

  const isOpen = isUnsavedDraft || expanded;
  const isEditable = isUnsavedDraft || editing;
  const isPlainDraft = isDraftSlot && Boolean(config.plainDraft);
  const headerLabel = effectiveRow ? config.getRowLabel(effectiveRow) : config.newEntryLabel;
  const showErrorIcon =
    !isOpen && (!formik.isValid || config.isDraftReady?.(effectiveRow) === false);

  if (isPlainDraft) {
    return (
      <Stack sx={[{ pb: 1 }, getAutoSaveFieldsSx(true)]}>
        {config.renderFields({
          formik,
          disabled,
          readOnly,
          saveField,
          backgroundColor: FORTA_GRAY1,
          row: effectiveRow,
        })}
      </Stack>
    );
  }

  return (
    <Stack sx={getAutoSaveRowSx(index, isEditable)}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="center" gap={1} flex={1}>
          {showErrorIcon ? (
            <ErrorIcon role="img" aria-label={`${headerLabel} has errors`} sx={{ color: FORTA_ERROR, fontSize: 16 }} />
          ) : null}
          <Typography>{headerLabel}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {isUnsavedDraft ? (
            <IconButton
              aria-label={`Remove ${config.newEntryLabel}`}
              // Discarding mid-create would orphan the row the create is about to persist.
              disabled={disabled || isCreating}
              onClick={onDiscardDraft}
              size="small"
              sx={AUTO_SAVE_ROW_ICON_BUTTON_SX}
            >
              <DeleteIconBordered width={36} height={36} />
            </IconButton>
          ) : (
            <>
              {readOnly ? null : (
                <>
                  <IconButton
                    aria-label={`Edit ${headerLabel}`}
                    disabled={disabled}
                    onClick={handleEdit}
                    size="small"
                    sx={[AUTO_SAVE_ROW_ICON_BUTTON_SX, editing ? AUTO_SAVE_ROW_ACTIVE_ICON_SX : {}]}
                  >
                    <EditIconBordered width={36} height={36} color={editing ? FORTA_WHITE : undefined} />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${headerLabel}`}
                    disabled={disabled}
                    onClick={handleDelete}
                    size="small"
                    sx={AUTO_SAVE_ROW_ICON_BUTTON_SX}
                  >
                    <DeleteIconBordered width={36} height={36} />
                  </IconButton>
                </>
              )}
              <IconButton
                aria-label={expanded ? `Collapse ${headerLabel}` : `Expand ${headerLabel}`}
                onClick={handleExpandToggle}
                size="small"
                sx={getAutoSaveExpandIconSx(expanded)}
              >
                <CollapseIconBordered
                  width={36}
                  height={36}
                  color={expanded ? FORTA_WHITE : undefined}
                />
              </IconButton>
            </>
          )}
        </Stack>
      </Stack>

      <Collapse in={isOpen}>
        <Stack sx={[{ pt: 2, pb: 1 }, getAutoSaveFieldsSx(isEditable)]}>
          {config.renderFields({
            formik,
            disabled,
            readOnly: !isEditable,
            saveField,
            // Input fill comes from getAutoSaveFieldsSx. backgroundColor is only used by the
            // legacy date-picker fill path (custom pickerStyle) so chrome still matches the
            // surface: grey while editing, the alternating row color while viewing.
            backgroundColor: isEditable ? FORTA_GRAY1 : LIST_ROW_BG_COLORS[index % 2],
            row: effectiveRow,
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
}
